import React, { useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
  GridRowSelectionModel,
} from "@mui/x-data-grid-premium";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  CircularProgress,
  Stack,
  Alert,
} from "@mui/material";
import { DocumentListItemDTO } from "../types/DocumentListItemDTO";
import {
  getDocumentListServerSide,
  bulkApproveDocuments,
  bulkRejectDocuments,
  fetchDocumentsFromKsef,
  updateProcessedExternally,
  updateProcessed,
  bulkUpdatePaymentStatus,
  getPaymentMethodOptions,
} from "../api/documents";
import DocumentsGrid from "../components/documents/DocumentsGrid";
import { getVendors } from "../api/vendors";
import { VendorDTO } from "../types/VendorDTO";
import AddInvoiceModal from "../components/documents/AddInvoiceModal";
import BulkPaymentStatusDialog from "../components/documents/BulkPaymentStatusDialog";
import { useAppTranslation } from "../hooks/useAppTranslation";
import {
  buildGridStateStorageKey,
  readGridControlledModels,
  writeGridControlledModels,
} from "../utils/gridStatePersistence";
import {
  OwnerOption,
  PaymentMethodOption,
  DocumentColumnPermissions,
} from "../components/documents/columns";
import { apiFetch } from "../api/apiFetch";
import {
  DOCUMENT_SERVER_SIDE_FILTERABLE_FIELDS,
  DOCUMENT_SERVER_SIDE_SORTABLE_FIELDS,
} from "../constants/documentServerSideFields";

const DEFAULT_DOCUMENTS_PAGINATION: GridPaginationModel = {
  page: 0,
  pageSize: 25,
};

const DEFAULT_DOCUMENTS_SORT: GridSortModel = [
  { field: "documentDate", sort: "desc" },
];

const DEFAULT_DOCUMENTS_FILTER: GridFilterModel = {
  items: [],
};

const sanitizeDocumentsSortModel = (model: GridSortModel): GridSortModel =>
  model
    .filter((item) => DOCUMENT_SERVER_SIDE_SORTABLE_FIELDS.has(item.field))
    .slice(0, 1);

const sanitizeDocumentsFilterModel = (model: GridFilterModel): GridFilterModel => ({
  ...model,
  items: (model.items ?? []).filter((item) =>
    DOCUMENT_SERVER_SIDE_FILTERABLE_FIELDS.has(item.field)
  ),
});

type BulkActionMode = "approve" | "reject";
type DocumentsViewMode = "all" | "pending" | "approved" | "rejected";

type Props = {
  companyId: string;
  viewMode?: DocumentsViewMode;
};

export default function DocumentsPage({ companyId, viewMode }: Props) {
  const { t } = useAppTranslation("common");
  const navigate = useNavigate();

  const baseFilter = useMemo(() => {
    switch (viewMode) {
      case "pending":
        return [
          {
            field: "status",
            operator: "isAnyOf",
            value: [
              "Draft",
              "Preliminary approval",
              "Preliminary_x0020_approval",
            ],
          },
        ];
      case "approved":
        return [{ field: "status", operator: "equals", value: "Approved" }];
      case "rejected":
        return [{ field: "status", operator: "equals", value: "Rejected" }];
      default:
        return [];
    }
  }, [viewMode]);

  const effectiveViewMode = viewMode ?? "all";

  const gridStorageKey = useMemo(
    () =>
      buildGridStateStorageKey({
        table: "documents",
        companyId,
        view: effectiveViewMode,
      }),
    [companyId, effectiveViewMode]
  );

  const initialGridModels = useMemo(() => {
    const models = readGridControlledModels(gridStorageKey, {
      paginationModel: DEFAULT_DOCUMENTS_PAGINATION,
      sortModel: DEFAULT_DOCUMENTS_SORT,
      filterModel: DEFAULT_DOCUMENTS_FILTER,
    });

    return {
      ...models,
      sortModel: sanitizeDocumentsSortModel(models.sortModel),
      filterModel: sanitizeDocumentsFilterModel(models.filterModel),
    };
  }, [gridStorageKey]);

  const [rows, setRows] = useState<DocumentListItemDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectionModel, setSelectionModel] =
    useState<GridRowSelectionModel>({
      type: "include",
      ids: new Set(),
    });

  const selectedIds = Array.from(selectionModel.ids) as string[];

  const [bulkMode, setBulkMode] = useState<BulkActionMode | null>(null);
  const [bulkComment, setBulkComment] = useState("");
  const [bulkLoading, setBulkLoading] = useState(false);
  
  const [bulkPaymentOpen, setBulkPaymentOpen] = useState(false);
  const [bulkPaymentLoading, setBulkPaymentLoading] = useState(false);

  const [paginationModel, setPaginationModel] =
    useState<GridPaginationModel>(initialGridModels.paginationModel);

  const [sortModel, setSortModel] =
    useState<GridSortModel>(initialGridModels.sortModel);

  const [filterModel, setFilterModel] =
    useState<GridFilterModel>(initialGridModels.filterModel);

  const [totalCount, setTotalCount] = useState(0);

  const persistGridModels = useCallback(
    (models: {
      paginationModel: GridPaginationModel;
      sortModel: GridSortModel;
      filterModel: GridFilterModel;
    }) => {
      writeGridControlledModels(gridStorageKey, models);
    },
    [gridStorageKey]
  );

  const handlePaginationModelChange = useCallback(
    (model: GridPaginationModel) => {
      setPaginationModel(model);

      persistGridModels({
        paginationModel: model,
        sortModel,
        filterModel,
      });
    },
    [filterModel, persistGridModels, sortModel]
  );

  const handleSortModelChange = useCallback(
    (model: GridSortModel) => {
      const nextSortModel = sanitizeDocumentsSortModel(model);
      const nextPaginationModel = {
        ...paginationModel,
        page: 0,
      };

      setSortModel(nextSortModel);
      setPaginationModel(nextPaginationModel);

      persistGridModels({
        paginationModel: nextPaginationModel,
        sortModel: nextSortModel,
        filterModel,
      });
    },
    [filterModel, paginationModel, persistGridModels]
  );

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel) => {
      const nextFilterModel = sanitizeDocumentsFilterModel(model);
      const nextPaginationModel = {
        ...paginationModel,
        page: 0,
      };

      setFilterModel(nextFilterModel);
      setPaginationModel(nextPaginationModel);

      persistGridModels({
        paginationModel: nextPaginationModel,
        sortModel,
        filterModel: nextFilterModel,
      });
    },
    [paginationModel, persistGridModels, sortModel]
  );

  const [vendors, setVendors] = useState<VendorDTO[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);

  const [addInvoiceOpen, setAddInvoiceOpen] = useState(false);
  const [ksefLoading, setKsefLoading] = useState(false);

  const [bulkMessage, setBulkMessage] = useState<{
    open: boolean;
    severity: "success" | "warning" | "error";
    message: string;
  }>({
    open: false,
    severity: "success",
    message: "",
  });

  const reloadDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const mergedFilterModel: GridFilterModel = {
        items: [...baseFilter, ...filterModel.items],
      };

      const result = await getDocumentListServerSide(
        companyId,
        paginationModel,
        sortModel,
        mergedFilterModel
      );

      setRows(result.items);
      setTotalCount(result.total);
    } catch (e) {
      console.error(e);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [companyId, paginationModel, sortModel, filterModel, baseFilter]);

  useEffect(() => {
    reloadDocuments();
  }, [reloadDocuments]);

  useEffect(() => {
    setVendorsLoading(true);
    getVendors(companyId)
      .then(setVendors)
      .finally(() => setVendorsLoading(false));
  }, [companyId]);

  const handleFetchFromKsef = async () => {
    try {
      setKsefLoading(true);
      await fetchDocumentsFromKsef(companyId);
      await reloadDocuments();
    } catch (err) {
      console.error(err);
      setBulkMessage({
        open: true,
        severity: "error",
        message: t("Nie udało się pobrać dokumentów z KSeF. Spróbuj ponownie później."),
      });
    } finally {
      setKsefLoading(false);
    }
  };

  useEffect(() => {
    if (!bulkMessage.open) return;

    const timer = setTimeout(() => {
      setBulkMessage((prev) => ({ ...prev, open: false }));
    }, 5000);

    return () => clearTimeout(timer);
  }, [bulkMessage.open]);

  const selectedRows = rows.filter((r) =>
    selectedIds.includes(r.id)
  );

  const currentPaymentStatuses = useMemo(() => {
    const map: Record<string, number> = {};

    selectedRows.forEach((doc) => {
      let status = doc.paymentStatus;

      // dokumenty z pustym statusem traktujemy jako "__EMPTY__"
      if (!status || status.trim() === "") {
        status = "__EMPTY__";
      }

      map[status] = (map[status] || 0) + 1;
    });

    return map;
  }, [selectedRows]);

  const hasPostedSelected = selectedRows.some(
    (r) => r.status === "Posted"
  );

  const handleToggleProcessedExternally = useCallback(
    async (row: DocumentListItemDTO, value: boolean) => {
      const previousValue = !!row.processedExternally;

      // Natychmiastowe odświeżenie UI.
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                processedExternally: value,
              }
            : r
        )
      );

      try {
        await updateProcessedExternally(row.id, companyId, value);

        // Dociągamy stan z BC, żeby tabela była spójna z backendem.
        await reloadDocuments();
      } catch (err) {
        console.error(err);

        // Rollback, jeśli PATCH się nie uda.
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  processedExternally: previousValue,
                }
              : r
          )
        );

        setBulkMessage({
          open: true,
          severity: "error",
          message: t("Nie udało się zmienić oznaczenia przetwarzania zewnętrznego."),
        });
      }
    },
    [companyId, reloadDocuments, t]
  );

  const handleToggleProcessed = useCallback(
    async (row: DocumentListItemDTO, value: boolean) => {
      const previousValue = !!row.processed;

      // Natychmiastowe odświeżenie UI.
      setRows((prev) =>
        prev.map((r) =>
          r.id === row.id
            ? {
                ...r,
                processed: value,
              }
            : r
        )
      );

      try {
        await updateProcessed(row.id, companyId, value);

        // Dociągamy stan z BC, żeby tabela była spójna z backendem.
        await reloadDocuments();
      } catch (err) {
        console.error(err);

        // Rollback, jeśli PATCH się nie uda.
        setRows((prev) =>
          prev.map((r) =>
            r.id === row.id
              ? {
                  ...r,
                  processed: previousValue,
                }
              : r
          )
        );

        setBulkMessage({
          open: true,
          severity: "error",
          message: t("Nie udało się zmienić oznaczenia przetworzenia dokumentu."),
        });
      }
    },
    [companyId, reloadDocuments, t]
  );

  const handleSelectionChange = (model: GridRowSelectionModel) => {
    if (model.type === "exclude") {
      const allIds = new Set(rows.map((r) => r.id));
      model.ids.forEach((id) => allIds.delete(String(id)));
      setSelectionModel({ type: "include", ids: allIds });
    } else {
      setSelectionModel(model);
    }
  };

  useEffect(() => {
    const models = readGridControlledModels(gridStorageKey, {
      paginationModel: DEFAULT_DOCUMENTS_PAGINATION,
      sortModel: DEFAULT_DOCUMENTS_SORT,
      filterModel: DEFAULT_DOCUMENTS_FILTER,
    });

    setPaginationModel(models.paginationModel);
    setSortModel(sanitizeDocumentsSortModel(models.sortModel));
    setFilterModel(sanitizeDocumentsFilterModel(models.filterModel));
    setSelectionModel({ type: "include", ids: new Set() });
  }, [gridStorageKey]);

  const [ownerOptions, setOwnerOptions] = useState<OwnerOption[]>([]);
  const [paymentMethodOptions, setPaymentMethodOptions] =
    useState<PaymentMethodOption[]>([]);

  const [documentColumnPermissions, setDocumentColumnPermissions] =
    useState<DocumentColumnPermissions>({
      canMarkProcessedExternally: false,
      canMarkProcessed: false,
    });

  const [documentGridContextReady, setDocumentGridContextReady] =
    useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadCompanyContext = async () => {
      setDocumentGridContextReady(false);

      try {
        const resolveRes = await apiFetch(
          `${process.env.REACT_APP_API_URL}/companies/resolve?bcCompanyId=${companyId}`
        );

        const resolved = await resolveRes.json();

        const [usersRes, permissionsRes, paymentMethods] = await Promise.all([
          apiFetch(
            `${process.env.REACT_APP_API_URL}/companies/${resolved.id}/users`
          ),
          apiFetch(
            `${process.env.REACT_APP_API_URL}/companies/${resolved.id}/document-column-permissions`
          ),
          getPaymentMethodOptions(companyId),
        ]);

        const users = await usersRes.json();
        const permissions = await permissionsRes.json();

        if (cancelled) {
          return;
        }

        setOwnerOptions(
          users.map((u: any) => ({
            value: u.email,
            label: u.display_name || u.email,
          }))
        );

        setPaymentMethodOptions(paymentMethods);

        setDocumentColumnPermissions({
          canMarkProcessedExternally:
            permissions.can_mark_processed_externally === true,
          canMarkProcessed:
            permissions.can_mark_processed === true,
        });
      } catch (err) {
        if (cancelled) {
          return;
        }

        console.error("Błąd pobierania kontekstu firmy", err);

        setOwnerOptions([]);
        setPaymentMethodOptions([]);

        setDocumentColumnPermissions({
          canMarkProcessedExternally: false,
          canMarkProcessed: false,
        });
      } finally {
        if (!cancelled) {
          setDocumentGridContextReady(true);
        }
      }
    };

    loadCompanyContext();

    return () => {
      cancelled = true;
    };
  }, [companyId]);

  return (
    <Stack sx={{ flex: 1, minHeight: 0, p: 2 }}>
      {bulkMessage.open && (
        <Alert
          severity={bulkMessage.severity}
          onClose={() =>
            setBulkMessage((prev) => ({ ...prev, open: false }))
          }
          sx={{ mb: 2 }}
        >
          {bulkMessage.message}
        </Alert>
      )}

      {documentGridContextReady ? (
        <DocumentsGrid
          gridStorageKey={gridStorageKey}
          rows={rows}
          loading={loading}
          rowCount={totalCount}
          selectedCount={selectedIds.length}
          onApprove={() => setBulkMode("approve")}
          onReject={() => setBulkMode("reject")}
          onBulkPaymentStatus={() => setBulkPaymentOpen(true)}
          onAddInvoice={() => setAddInvoiceOpen(true)}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          filterModel={filterModel}
          onFilterModelChange={handleFilterModelChange}
          selectionModel={selectionModel}
          onSelectionModelChange={handleSelectionChange}
          onRowClick={(id) =>
            navigate(`/document/${id}?companyId=${companyId}`)
          }
          onFetchFromKsef={handleFetchFromKsef}
          ksefLoading={ksefLoading}
          onToggleProcessedExternally={handleToggleProcessedExternally}
          onToggleProcessed={handleToggleProcessed}
          hasPostedSelected={hasPostedSelected}
          ownerOptions={ownerOptions}
          paymentMethodOptions={paymentMethodOptions}
          documentColumnPermissions={documentColumnPermissions}
        />
      ) : (
        <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
          <CircularProgress />
        </Stack>
      )}

      <Dialog
        open={bulkMode !== null}
        onClose={() => !bulkLoading && setBulkMode(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {bulkMode === "approve"
            ? t("Akceptacja dokumentów")
            : t("Odrzucenie dokumentów")}
        </DialogTitle>

        <DialogContent>
          {t("Wybrane dokumenty")}: <strong>{selectedIds.length}</strong>

          {bulkMode === "reject" && (
            <TextField
              autoFocus
              margin="dense"
              label={t("Powód odrzucenia")}
              fullWidth
              multiline
              minRows={3}
              value={bulkComment}
              onChange={(e) => setBulkComment(e.target.value)}
              disabled={bulkLoading}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setBulkMode(null)} disabled={bulkLoading}>
            {t("Anuluj")}
          </Button>

          <Button
            variant="contained"
            color={bulkMode === "approve" ? "primary" : "error"}
            disabled={
              bulkLoading ||
              (bulkMode === "reject" && !bulkComment.trim())
            }
            onClick={async () => {
              try {
                setBulkLoading(true);

                if (bulkMode === "approve") {
                  const response = await bulkApproveDocuments(
                    companyId,
                    selectedIds
                  );

                  const mpkFailures = response.failedDueToMPK?.length ?? 0;
                  const matchingFailures =
                    response.failedDueToPurchaseMatching?.length ?? 0;

                  if (response.failed > 0) {
                    const reasons: string[] = [];

                    if (matchingFailures > 0) {
                      reasons.push(
                        t("{{count}} dokumentów ma niekompletne dopasowanie ZZ/PZ.", {
                          count: matchingFailures,
                        })
                      );
                    }

                    if (mpkFailures > 0) {
                      reasons.push(
                        t("{{count}} dokumentów nie spełnia warunków MPK.", {
                          count: mpkFailures,
                        })
                      );
                    }

                    setBulkMessage({
                      open: true,
                      severity: "warning",
                      message:
                        reasons.length > 0
                          ? reasons.join(" ")
                          : t("Część dokumentów nie została zatwierdzona."),
                    });
                  } else {
                    setBulkMessage({
                      open: true,
                      severity: "success",
                      message: t("Dokumenty zostały zatwierdzone."),
                    });
                  }
                } else {
                  await bulkRejectDocuments(
                    companyId,
                    selectedIds,
                    bulkComment
                  );
                }

                setBulkMode(null);
                setBulkComment("");
                setSelectionModel({ type: "include", ids: new Set() });

                await reloadDocuments();
              } catch (err) {
                console.error(err);
                setBulkMessage({
                  open: true,
                  severity: "error",
                  message: t("Wystąpił błąd podczas operacji."),
                });
              } finally {
                setBulkLoading(false);
              }
            }}
          >
            {bulkLoading ? (
              <CircularProgress size={20} />
            ) : bulkMode === "approve" ? (
              t("Akceptuj")
            ) : (
              t("Odrzuć")
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <BulkPaymentStatusDialog
        open={bulkPaymentOpen}
        loading={bulkPaymentLoading}
        selectedCount={selectedIds.length}
        currentStatuses={currentPaymentStatuses}
        onClose={() => setBulkPaymentOpen(false)}
        onConfirm={async (paymentStatus) => {
          try {
            setBulkPaymentLoading(true);

            const resp = await bulkUpdatePaymentStatus(companyId, selectedIds, paymentStatus);

            // Możesz tu użyć resp.success / resp.failed do komunikatu:
            if (resp?.failed > 0) {
              setBulkMessage({
                open: true,
                severity: "warning",
                message: t("Zmieniono status płatności dla {{success}} dokumentów. Błędy: {{failed}}.", { success: resp.success, failed: resp.failed }),
              });
            } else {
              setBulkMessage({
                open: true,
                severity: "success",
                message: t("Status płatności został zmieniony dla wybranych dokumentów."),
              });
            }

            setBulkPaymentOpen(false);
            setSelectionModel({ type: "include", ids: new Set() });

            // Najprościej i najpewniej: odśwież listę (żeby dostać spójne dane z BC)
            await reloadDocuments();
          } catch (err) {
            console.error(err);
            setBulkMessage({
              open: true,
              severity: "error",
              message: t("Nie udało się zmienić statusu płatności."),
            });
          } finally {
            setBulkPaymentLoading(false);
          }
        }}
      />

      <AddInvoiceModal
        open={addInvoiceOpen}
        onClose={() => setAddInvoiceOpen(false)}
        companyId={companyId}
        vendors={vendors}
        vendorsLoading={vendorsLoading}
        onSuccess={() => {
          setAddInvoiceOpen(false);
          reloadDocuments();
        }}
      />
    </Stack>
  );
}